import type { CSSProperties } from "react";
import classnames from "classnames";
import { Link, useLocation } from "react-router-dom";

import Tool from "@/features/dashboard/components/tool/tool";
import type { WarningColor } from "@/features/shared/hooks/useHighestWarning";

import styles from "./warning-tool.module.css";

interface WarningToolLinkProps {
  to: string;
  color: WarningColor;
  label: string;
  /** Level for screen readers; a custom `label` may hide it from the text. */
  indicatorLabel: string;
  /** Overrides the color-class dot fill (land uses the risk-picture badge color). */
  indicatorColor?: CSSProperties["background"];
}

/** Toolbar shortcut to a warning page. Marks itself `aria-current` on that page. */
export function WarningToolLink({
  to,
  color,
  label,
  indicatorLabel,
  indicatorColor,
}: WarningToolLinkProps) {
  const { pathname } = useLocation();

  return (
    <Link
      to={to}
      aria-current={pathname === to ? "page" : undefined}
      className={classnames(styles.warning, styles[color])}
    >
      <Tool variant={color}>
        <span
          className={styles.indicator}
          style={indicatorColor ? { background: indicatorColor } : undefined}
          role="img"
          aria-label={indicatorLabel}
        />
        <span>{label}</span>
      </Tool>
    </Link>
  );
}
